import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Mail, MessageCircle, Loader2, Calendar, BedDouble, Users, Printer, Share2 } from "lucide-react";
import { toast } from "sonner";

const formatItalianDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

// Rooms are stored as i18n dicts (`{it, en, de, fr}`) in MongoDB. The
// backend tries to flatten them before returning, but we keep this guard
// here too so the gift page can't crash again with React error #31 ("Objects
// are not valid as a React child") if a future endpoint accidentally leaks
// the raw object.
const safeRoomName = (name) => {
  if (!name) return "Camera";
  if (typeof name === "string") return name;
  if (typeof name === "object") {
    return name.it || name.en || name.de || name.fr || Object.values(name)[0] || "Camera";
  }
  return String(name);
};

const formatShort = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch { return iso; }
};

export default function GiftTicketPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get(`/public/gift/${token}`)
      .then((res) => mounted && setData(res.data))
      .catch((err) => mounted && setError(err.response?.data?.detail || "Ticket non trovato"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [token]);

  const handlePrint = () => window.print();
  const handleShare = async () => {
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Il tuo ticket regalo · B&B Emmanuel", url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copiato negli appunti");
      } catch {
        toast.error("Impossibile copiare il link");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#AC5D45]" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA] p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <h1 className="text-2xl font-serif text-[#2C2A26] mb-3">Ticket non disponibile</h1>
          <p className="text-[#635F59]">{error || "Questo ticket regalo non esiste o non è più valido."}</p>
        </div>
      </div>
    );
  }

  const g = data.gift_ticket || {};
  const style = g.style || "boarding";
  const isBoarding = style === "boarding";
  const recipient = g.recipient_name || "Caro ospite";
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.property.address)}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F1EA] via-[#EFE7DA] to-[#E4D5C0] py-8 px-4 print:bg-white print:py-0 print:px-0">
      {/* Top action bar — hidden on print */}
      <div className="max-w-2xl mx-auto mb-6 flex items-center justify-between gap-3 print:hidden">
        <div className="text-[#635F59] text-sm">
          <span className="font-semibold text-[#AC5D45]">Ticket Regalo</span> · B&B Emmanuel
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleShare} data-testid="gift-share-btn" className="border-[#AC5D45] text-[#AC5D45] hover:bg-[#AC5D45]/10">
            <Share2 className="w-4 h-4 mr-1.5" /> Condividi
          </Button>
          <Button size="sm" onClick={handlePrint} data-testid="gift-print-btn" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
            <Printer className="w-4 h-4 mr-1.5" /> Stampa / PDF
          </Button>
        </div>
      </div>

      {isBoarding ? (
        <BoardingPassTicket data={data} gift={g} recipient={recipient} mapsUrl={mapsUrl} />
      ) : (
        <ClassicTicket data={data} gift={g} recipient={recipient} mapsUrl={mapsUrl} />
      )}

      <p className="max-w-2xl mx-auto text-center text-xs text-[#8A8680] mt-6 print:hidden">
        Vuoi tenerlo come ricordo? Tocca <span className="font-semibold">Stampa</span> e salva come PDF dal tuo dispositivo.
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Boarding-Pass style (horizontal-ish, elegant, modern)
   ───────────────────────────────────────────────────────────────────────── */
function BoardingPassTicket({ data, gift, recipient, mapsUrl }) {
  // Deep link to the public check-in form — the recipient can tap
  // the booking code (or the dedicated CTA below the QR) to fill in
  // their police-required data before arrival. Mirrors ClassicTicket
  // so both voucher styles offer the same shortcut.
  const checkinUrl = `/checkin/complete?code=${encodeURIComponent(data.code || "")}`;
  const qrValue = mapsUrl;
  const sender = data.sender_label || "";
  return (
    <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-[#E4D5C0]/60 print:shadow-none print:border print:rounded-none">
      {/* Top band */}
      <div className="bg-[#2C2A26] text-white px-6 py-5">
        {sender ? (
          <div className="text-center mb-3 pb-3 border-b border-white/15">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-60">Un regalo da</div>
            <div className="font-serif text-lg md:text-xl mt-1" data-testid="gift-sender">{sender}</div>
          </div>
        ) : null}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-60">Ticket Regalo</div>
            <div className="font-serif text-xl mt-0.5">B&B Emmanuel</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.2em] uppercase opacity-60">Codice</div>
            <a
              href={checkinUrl}
              data-testid="gift-code"
              className="font-mono text-base tracking-wide mt-0.5 inline-block underline decoration-dotted decoration-white/40 underline-offset-2 hover:decoration-solid hover:text-[#E4D5C0] transition"
              title="Apri il check-in: completa i tuoi dati prima dell'arrivo"
            >
              {data.code}
            </a>
          </div>
        </div>
      </div>

      {/* Recipient + message */}
      <div className="px-6 pt-6 pb-2 border-b border-dashed border-[#E4D5C0]">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#AC5D45]">Per</div>
        <div className="font-serif text-2xl md:text-3xl text-[#2C2A26] mt-1" data-testid="gift-recipient">{recipient}</div>
        {gift.message ? (
          <p className="text-[#635F59] italic mt-3 text-sm md:text-base whitespace-pre-wrap" data-testid="gift-message">
            &ldquo;{gift.message}&rdquo;
          </p>
        ) : null}
      </div>

      {/* Dates row */}
      <div className="px-6 py-5 grid grid-cols-3 gap-3 items-center">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#8A8680]">Check-in</div>
          <div className="font-serif text-lg text-[#2C2A26] mt-1 capitalize">{formatShort(data.check_in)}</div>
        </div>
        <div className="flex flex-col items-center text-[#AC5D45]">
          <div className="text-[10px] tracking-[0.2em] uppercase opacity-70">{data.nights || "—"} notte/i</div>
          <div className="relative w-full mt-1">
            <div className="h-px bg-[#E4D5C0] mt-2"></div>
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-lg">✈</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#8A8680]">Check-out</div>
          <div className="font-serif text-lg text-[#2C2A26] mt-1 capitalize">{formatShort(data.check_out)}</div>
        </div>
      </div>

      {/* Details + QR */}
      <div className="border-t border-dashed border-[#E4D5C0] px-6 py-5 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-6 items-center">
        <div className="space-y-2.5 text-sm">
          <Row icon={<BedDouble className="w-4 h-4" />} label="Camera" value={data.room.name} />
          <Row icon={<Users className="w-4 h-4" />} label="Ospiti" value={`${data.guests} ${data.guests > 1 ? "persone" : "persona"}`} />
          <Row icon={<MapPin className="w-4 h-4" />} label="Indirizzo" value={data.property.address} />
        </div>
        <div className="flex flex-col items-center md:items-end gap-2">
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 bg-white border border-[#E4D5C0] rounded-lg hover:border-[#AC5D45] transition"
            data-testid="gift-qr"
            title="Apri in Google Maps"
          >
            <QRCodeSVG value={qrValue} size={92} level="M" />
          </a>
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-[#AC5D45] text-center md:text-right underline decoration-dotted hover:decoration-solid"
            data-testid="gift-maps-link"
          >
            Apri in Google Maps →
          </a>
        </div>
      </div>

      {/* Check-in invitation — explicit CTA so the recipient knows the
          gift code can also be used to pre-fill their police data.
          Hidden in print mode (it's an action button). */}
      <div className="bg-[#FDFBF7] border-t border-dashed border-[#E4D5C0] px-6 py-4 print:hidden" data-testid="gift-checkin-block">
        <p className="text-[11px] text-[#635F59] text-center mb-2">
          🛬 Prima dell'arrivo: compila i tuoi dati per il check-in (1 minuto)
        </p>
        <a
          href={checkinUrl}
          data-testid="gift-checkin-cta"
          className="block text-center bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full px-5 py-2.5 text-sm font-semibold transition"
        >
          Vai al check-in →
        </a>
      </div>

      {/* Footer contacts */}
      <div className="bg-[#F5F1EA] border-t border-[#E4D5C0]/60 px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <a href={`tel:${data.property.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-[#2C2A26] hover:text-[#AC5D45]" data-testid="gift-contact-phone">
          <Phone className="w-3.5 h-3.5 text-[#AC5D45]" /> {data.property.phone}
        </a>
        <a href={`https://wa.me/${data.property.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-white bg-[#25D366] hover:bg-[#1FAA52] rounded-md px-2 py-1.5 justify-center font-semibold transition" data-testid="gift-contact-whatsapp">
          <MessageCircle className="w-3.5 h-3.5" /> Chatta su WhatsApp
        </a>
        <a href={`mailto:${data.property.email}`} className="flex items-center gap-2 text-[#2C2A26] hover:text-[#AC5D45]" data-testid="gift-contact-email">
          <Mail className="w-3.5 h-3.5 text-[#AC5D45]" /> {data.property.email}
        </a>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Classic vertical style (logo + decorative frame, traditional invite)
   ───────────────────────────────────────────────────────────────────────── */
function ClassicTicket({ data, gift, recipient, mapsUrl }) {
  const sender = data.sender_label || "";
  // Same deep link the BoardingPassTicket uses — keep both styles in
  // sync so the recipient finds the check-in entry point regardless
  // of which voucher layout the host chose.
  const checkinUrl = `/checkin/complete?code=${encodeURIComponent(data.code || "")}`;
  return (
    <div className="max-w-md mx-auto bg-[#FBF8F2] rounded-2xl shadow-2xl border-[6px] border-double border-[#AC5D45] overflow-hidden print:shadow-none print:rounded-none">
      {/* Sentimental block — sender · recipient · message */}
      <div className="text-center px-7 pt-8 pb-3">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#AC5D45]">— Un regalo da —</div>
        {sender ? (
          <div className="font-serif italic text-2xl text-[#2C2A26] mt-2" data-testid="gift-sender">{sender}</div>
        ) : null}
        <div className="text-[#8A8680] italic text-xs tracking-wide mt-3">per</div>
        <div className="font-serif italic text-2xl text-[#2C2A26] mt-1" data-testid="gift-recipient">{recipient}</div>
        {gift.message ? (
          <p className="text-[#635F59] italic mt-3 text-sm whitespace-pre-wrap" data-testid="gift-message">&ldquo;{gift.message}&rdquo;</p>
        ) : null}
      </div>

      {/* Brand divider — smaller B&B header introducing the stay details */}
      <div className="text-center px-7 py-3 bg-[#AC5D45]/5 border-y border-[#AC5D45]/30">
        <h2 className="font-serif italic text-xl text-[#2C2A26]">B&amp;B Emmanuel</h2>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#8A8680] mt-0.5">Reggio Calabria</div>
      </div>

      <div className="px-7 py-4 space-y-2.5 text-sm">
        <Row icon={<Calendar className="w-4 h-4" />} label="Arrivo" value={formatItalianDate(data.check_in)} />
        <Row icon={<Calendar className="w-4 h-4" />} label="Partenza" value={formatItalianDate(data.check_out)} />
        <Row icon={<BedDouble className="w-4 h-4" />} label="Camera" value={safeRoomName(data.room?.name)} />
        <Row icon={<Users className="w-4 h-4" />} label="Ospiti" value={`${data.guests} ${data.guests > 1 ? "persone" : "persona"}`} />
        <Row icon={<MapPin className="w-4 h-4" />} label="Indirizzo" value={data.property.address} />
      </div>

      <div className="px-7 py-3 text-center border-t border-dashed border-[#AC5D45]/40">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#8A8680]">Codice Prenotazione</div>
        <a
          href={checkinUrl}
          data-testid="gift-code"
          className="font-mono text-lg tracking-wider text-[#2C2A26] mt-1 inline-block underline decoration-dotted decoration-[#AC5D45]/50 underline-offset-2 hover:decoration-solid"
          title="Apri il check-in: completa i tuoi dati prima dell'arrivo"
        >
          {data.code}
        </a>
      </div>

      <div className="px-7 pb-5 flex flex-col items-center gap-2">
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 bg-white border border-[#E4D5C0] rounded-lg hover:border-[#AC5D45] transition"
          data-testid="gift-qr"
          title="Apri in Google Maps"
        >
          <QRCodeSVG value={mapsUrl} size={96} level="M" />
        </a>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-[#AC5D45] underline decoration-dotted hover:decoration-solid"
          data-testid="gift-maps-link"
        >
          Apri in Google Maps →
        </a>
      </div>

      {/* Check-in invitation — mirrors the boarding-pass CTA so the
          recipient can pre-fill their police data straight from the
          voucher (hidden in print). */}
      <div className="px-7 pb-4 print:hidden" data-testid="gift-checkin-block">
        <p className="text-[11px] text-[#635F59] text-center mb-2 italic">
          🛬 Prima dell'arrivo: compila i tuoi dati per il check-in (1 minuto)
        </p>
        <a
          href={checkinUrl}
          data-testid="gift-checkin-cta"
          className="block text-center bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full px-5 py-2.5 text-sm font-semibold transition"
        >
          Vai al check-in →
        </a>
      </div>

      <div className="px-7 py-4 text-center text-xs space-y-1.5">
        <a href={`tel:${data.property.phone.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 bg-[#AC5D45] text-white rounded-md py-2 hover:bg-[#8A4936] transition" data-testid="gift-contact-phone">
          <Phone className="w-3.5 h-3.5" /> {data.property.phone}
        </a>
        <a href={`https://wa.me/${data.property.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-md py-2 hover:bg-[#1FAA52] transition font-semibold" data-testid="gift-contact-whatsapp">
          <MessageCircle className="w-3.5 h-3.5" /> Chatta su WhatsApp
        </a>
        <a href={`mailto:${data.property.email}`} className="flex items-center justify-center gap-2 text-[#2C2A26] hover:text-[#AC5D45] pt-1" data-testid="gift-contact-email">
          <Mail className="w-3.5 h-3.5 text-[#AC5D45]" /> {data.property.email}
        </a>
      </div>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-[#AC5D45] mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#8A8680]">{label}</div>
        <div className="text-[#2C2A26]">{value}</div>
      </div>
    </div>
  );
}
